<script setup lang="ts">
  import { computed } from 'vue';
  import { SelectionSection, CustomCheckbox, MemberNameStatus } from '@/shared/ui';
  import { isActiveUser } from '@/shared/lib';
  import type {
    PositionUserSelectorProps,
    ToggleSelectionProps,
    UserItem,
  } from './PositionUserSelector.types';

  const props = withDefaults(defineProps<PositionUserSelectorProps>(), {
    userSelectionIdType: 'memberId',
    showMemberStatus: false,
    showUserRegistration: false,
  });

  const getUserDisplayName = (item: UserItem): string => {
    const name = item.name ?? '';
    const registration = item.registrationNumber?.trim();
    return props.showUserRegistration && registration ? `${name} - ${registration}` : name;
  };

  const emit = defineEmits<{
    (e: 'update:selectedPositions', value: string[]): void;
    (e: 'update:selectedUsers', value: string[]): void;
  }>();

  type SelectionEmitName = 'update:selectedPositions' | 'update:selectedUsers';

  const emitSelectionUpdate = (emitValue: SelectionEmitName, payload: string[]) => {
    if (emitValue === 'update:selectedPositions') {
      emit('update:selectedPositions', payload);
    } else {
      emit('update:selectedUsers', payload);
    }
  };

  const getUserSelectionId = (item: UserItem): string => {
    if (props.userSelectionIdType === 'memberId') {
      const memberId = item.membership?.id ?? item.memberId;
      return memberId?.toString() ?? item.id?.toString() ?? '';
    }
    const userId = item.userId ?? item.id;
    return userId?.toString() ?? '';
  };

  const getAllPositionNames = (item: UserItem): string[] => {
    const names = (item.membership?.positions ?? []).map((p) => p.name);
    if (names.length > 0) return names;
    if (item.positions?.length) {
      return item.positions.map((p) => p.name);
    }
    if (item.userPositions?.[0]?.position?.name) {
      return [item.userPositions[0].position.name];
    }
    return [];
  };

  const getItemId = (item: { id?: number | string } | undefined): string =>
    item?.id !== undefined && item.id !== null ? String(item.id) : '';

  const positionNamesMap = computed(() => {
    const map = new Map<string, string[]>();
    for (const item of props.users.data ?? []) {
      map.set(item.id?.toString() ?? '', getAllPositionNames(item));
    }
    return map;
  });

  const toggleSelection = ({ e, emitValue, selected, item }: ToggleSelectionProps) => {
    e.stopPropagation();

    const itemId =
      emitValue === 'update:selectedUsers'
        ? getUserSelectionId(item as UserItem)
        : getItemId(item as { id?: number | string });

    const isChecked = (e.target as HTMLInputElement).checked;

    let nextSelection: string[];
    if (isChecked) {
      nextSelection = [...selected, itemId];
    } else {
      nextSelection = selected.filter((id) => id !== itemId);
    }

    emitSelectionUpdate(emitValue, nextSelection);
  };

  const handleSelectedUsersUpdate = (selectedIds: string[]) => {
    emitSelectionUpdate('update:selectedUsers', selectedIds);
  };

  const handleSelectedPositionsUpdate = (selectedIds: string[]) => {
    emitSelectionUpdate('update:selectedPositions', selectedIds);
  };
</script>

<template>
  <div class="flex m-auto gap-8 w-full">
    <SelectionSection
      :selection-class="selectionClass"
      title="Cargos"
      select-all-label="Selecionar todos os cargos"
      :hide-selection="hideSelection"
      :items="positions.data || []"
      :selected-items="selectedPositions"
      :count="positionsSelectedCount"
      count-label="Total de cargos selecionados:"
      @update:selected-items="handleSelectedPositionsUpdate"
    >
      <template #columns>
        <th class="text-left font-semibold pb-4">Cargos</th>
      </template>

      <template #item="{ item }">
        <td class="pb-3 flex gap-3 items-center">
          <CustomCheckbox
            :id="getItemId(item)"
            :value="getItemId(item)"
            :disabled="hideSelection"
            :checked="selectedPositions.includes(getItemId(item))"
            @change="
              (e: Event) =>
                toggleSelection({
                  e,
                  emitValue: 'update:selectedPositions',
                  selected: selectedPositions,
                  item,
                })
            "
          />
          {{ item.name }}
        </td>
      </template>
    </SelectionSection>

    <SelectionSection
      :selection-class="selectionClass"
      title="Colaborador"
      :hide-selection="hideSelection || selectedPositions.length === 0"
      select-all-label="Selecionar todos os colaboradores"
      :items="users.data || []"
      :selected-items="selectedUsers"
      :count="usersSelectedCount"
      count-label="Total de colaboradores selecionados:"
      :get-item-id="getUserSelectionId"
      @update:selected-items="handleSelectedUsersUpdate"
    >
      <template #columns>
        <th class="text-left font-semibold pb-4">Colaborador</th>
        <th class="text-left font-semibold pb-4">Cargo</th>
      </template>

      <template #item="{ item }">
        <td class="pb-4 flex gap-3 items-center">
          <CustomCheckbox
            :id="getUserSelectionId(item)"
            :value="getUserSelectionId(item)"
            :disabled="hideSelection"
            :checked="selectedUsers.includes(getUserSelectionId(item))"
            @change="
              (e: Event) =>
                toggleSelection({
                  e,
                  emitValue: 'update:selectedUsers',
                  selected: selectedUsers,
                  item,
                })
            "
          />
          <MemberNameStatus
            v-if="showMemberStatus"
            :name="item.name ?? ''"
            :active="isActiveUser(item)"
          />
          <template v-else>
            {{ getUserDisplayName(item) }}
          </template>
        </td>
        <td class="pb-4">
          <div
            v-tippy="
              (positionNamesMap.get(getItemId(item)) ?? []).length > 3
                ? {
                    content: (positionNamesMap.get(getItemId(item)) ?? []).slice(3).join('<br>'),
                    allowHTML: true,
                    placement: 'top',
                    theme: 'custom',
                  }
                : undefined
            "
          >
            <span
              v-for="(name, idx) in (positionNamesMap.get(getItemId(item)) ?? []).slice(0, 3)"
              :key="idx"
              class="block"
            >
              {{ name
              }}{{
                idx === 2 && (positionNamesMap.get(getItemId(item)) ?? []).length > 3 ? '...' : ''
              }}
            </span>
          </div>
        </td>
      </template>
    </SelectionSection>
  </div>
</template>
